import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Ticket, Calendar, Users, IndianRupee, MessageSquare,
  ArrowUpRight, TrendingUp, Clock, AlertCircle
} from 'lucide-react';
import { TiltCard, GlowingCard } from '../../components/3DEffects';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recentTickets, setRecentTickets] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, ticketsRes, bookingsRes] = await Promise.all([
        fetch(`${API}/admin/stats`, { credentials: 'include' }),
        fetch(`${API}/admin/tickets`, { credentials: 'include' }),
        fetch(`${API}/admin/bookings`, { credentials: 'include' })
      ]);

      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      if (ticketsRes.ok) {
        const tickets = await ticketsRes.json();
        setRecentTickets(tickets.slice(0, 5));
      }
      if (bookingsRes.ok) {
        const bookings = await bookingsRes.json();
        setRecentBookings(bookings.slice(0, 5));
      }
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'text-yellow-400 bg-yellow-400/10',
      in_progress: 'text-blue-400 bg-blue-400/10',
      resolved: 'text-green-400 bg-green-400/10',
      closed: 'text-gray-400 bg-gray-400/10',
      pending: 'text-yellow-400 bg-yellow-400/10',
      confirmed: 'text-blue-400 bg-blue-400/10',
      completed: 'text-green-400 bg-green-400/10',
      cancelled: 'text-red-400 bg-red-400/10',
      new: 'text-blue-400 bg-blue-400/10'
    };
    return colors[status] || 'text-gray-400 bg-gray-400/10';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const statCards = [
    { 
      label: 'Open Tickets', 
      value: stats?.open_tickets || 0, 
      total: stats?.total_tickets || 0,
      icon: Ticket, 
      color: 'from-yellow-500/20 to-orange-500/20',
      iconColor: 'text-yellow-400',
      link: '/admin/tickets'
    },
    { 
      label: 'Pending Bookings', 
      value: stats?.pending_bookings || 0,
      total: stats?.total_bookings || 0,
      icon: Calendar, 
      color: 'from-blue-500/20 to-cyan-500/20',
      iconColor: 'text-blue-400',
      link: '/admin/bookings'
    },
    { 
      label: 'Total Users', 
      value: stats?.total_users || 0,
      icon: Users, 
      color: 'from-purple-500/20 to-pink-500/20',
      iconColor: 'text-purple-400',
      link: '/admin/users'
    },
    { 
      label: 'Revenue', 
      value: `₹${(stats?.total_revenue || 0).toLocaleString()}`,
      icon: IndianRupee, 
      color: 'from-green-500/20 to-emerald-500/20',
      iconColor: 'text-green-400'
    },
    { 
      label: 'New Inquiries', 
      value: stats?.new_inquiries || 0,
      total: stats?.total_inquiries || 0,
      icon: MessageSquare, 
      color: 'from-blue-500/20 to-indigo-500/20',
      iconColor: 'text-blue-400',
      link: '/admin/inquiries'
    }
  ];

  return (
    <div data-testid="admin-dashboard">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">Admin Dashboard</h1>
        <p className="text-gray-400">Overview of your AMC business operations</p>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {statCards.map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
            >
              <TiltCard intensity={5}>
                <GlowingCard className="p-5 rounded-xl h-full">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${stat.color} flex items-center justify-center mb-3`}>
                    <Icon className={`w-5 h-5 ${stat.iconColor}`} />
                  </div>
                  <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{stat.label}</span>
                    {stat.total !== undefined && (
                      <span className="text-xs text-gray-600">/ {stat.total} total</span>
                    )}
                  </div>
                  {stat.link && (
                    <Link 
                      to={stat.link}
                      className="absolute top-4 right-4 text-gray-500 hover:text-blue-400 transition-colors"
                    >
                      <ArrowUpRight className="w-4 h-4" />
                    </Link>
                  )}
                </GlowingCard>
              </TiltCard>
            </motion.div>
          );
        })}
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Tickets */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <GlowingCard className="rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Ticket className="w-5 h-5 text-blue-400" />
                Recent Tickets
              </h2>
              <Link to="/admin/tickets" className="text-blue-400 text-sm hover:underline">
                View All
              </Link>
            </div>

            {recentTickets.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <AlertCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No tickets yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentTickets.map((ticket) => (
                  <Link
                    key={ticket.ticket_id}
                    to={`/admin/tickets/${ticket.ticket_id}`}
                    className="block p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white text-sm font-medium truncate">{ticket.subject}</h4>
                        <p className="text-gray-500 text-xs truncate">{ticket.user?.email}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                        {ticket.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-600">
                      <span>{ticket.category}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(ticket.created_at)}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </GlowingCard>
        </motion.div>

        {/* Recent Bookings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <GlowingCard className="rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-400" />
                Recent Bookings
              </h2>
              <Link to="/admin/bookings" className="text-blue-400 text-sm hover:underline">
                View All
              </Link>
            </div>

            {recentBookings.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No bookings yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentBookings.map((booking) => (
                  <div
                    key={booking.booking_id}
                    className="p-3 rounded-lg bg-white/5"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white text-sm font-medium">
                          {booking.package?.name || booking.package_id}
                        </h4>
                        <p className="text-gray-500 text-xs truncate">{booking.user?.email}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                        {booking.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-600">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(booking.scheduled_date)}
                      </span>
                      {booking.package?.price && (
                        <span className="text-green-400">₹{booking.package.price}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </GlowingCard>
        </motion.div>
      </div>
    </div>
  );
}
